/* eslint-disable no-restricted-globals */
import {
  APP_INITIALIZER,
  inject,
  Injectable,
  Injector,
  makeStateKey,
  REQUEST,
  TransferState,
  type Provider,
} from '@angular/core';
import type { ActivatedRouteSnapshot } from '@angular/router';
import {
  bufferTime,
  concatMap,
  delayWhen,
  EMPTY,
  filter,
  map,
  of,
  take,
  takeUntil,
} from 'rxjs';

import {
  CosAnalyticsService,
  isDestinationEnabled$,
  type StatDestination,
  type TrackEvent,
  type TrackEventData,
} from '@cosmos/analytics/common';
// TODO(ds): imports between cosmos/config and cosmos/analytics should be refactored
// eslint-disable-next-line @nx/enforce-module-boundaries
import { injectConfigProperty } from '@cosmos/config';
import { unpatchedAsyncScheduler, unpatchedFromEvent } from '@cosmos/zone-less';

import { AsiStatsSender } from './asi-stats-sender';
import type { AsiStatsConfig } from './types';

@Injectable({ providedIn: 'root' })
export class AsiStatsService {
  private readonly _injector = inject(Injector);
  private readonly _ipAddress = inject(TransferState).get(
    ipAddressStateKey,
    null
  );
  protected readonly _analytics = inject(CosAnalyticsService);
  private readonly _asiStatsConfig: AsiStatsConfig =
    injectConfigProperty('asiStats');

  private readonly _unloadEvent$ = global_isBrowser
    ? unpatchedFromEvent(document, 'beforeunload')
    : EMPTY;

  private _sender: AsiStatsSender | null = null;

  /** Notifier to delay the start of event emission. Is being used in subclasses */
  protected readonly readyToEmit$ = of(true);

  initListeners(): void {
    this._setupRouterEventsListener();
    this._setupTrackEventsListener();
  }

  private _setupRouterEventsListener(): void {
    if (!this._asiStatsConfig.trackPageEvents) return;

    this._analytics.pageEvent$
      .pipe(
        concatMap((pageEvent) =>
          this._analytics.userEvent$.pipe(
            filter((user) => !!user?.traits),
            take(1),
            map(() => pageEvent)
          )
        ),
        delayWhen(() => this.readyToEmit$.pipe(filter(Boolean), take(1)))
      )
      .subscribe(({ snapshot, previousReferrerUrl }) => {
        this._page(snapshot, previousReferrerUrl);
      });
  }

  private _setupTrackEventsListener(): void {
    const destinations: StatDestination[] = ['asi-stats', 'segment'];

    this._analytics.trackEvent$
      .pipe(
        // Flush the buffer if the user closes the browser
        // eslint-disable-next-line rxjs/no-unsafe-takeuntil
        takeUntil(this._unloadEvent$),
        isDestinationEnabled$(destinations),
        delayWhen(() => this.readyToEmit$.pipe(filter(Boolean), take(1))),
        bufferTime(
          this._asiStatsConfig.buffer?.timeSpan ?? 5000,
          null,
          this._asiStatsConfig.buffer?.maxSize ?? 5,
          unpatchedAsyncScheduler
        )
      )
      // No reason to unsubscribe because this is the root browser provider,
      // which will be disposed of when the tab is closed.
      // eslint-disable-next-line rxjs-angular/prefer-takeuntil
      .subscribe((events) => {
        if (!events?.length) return;
        this._track(events);
      });
  }

  private _page(
    snapshot: ActivatedRouteSnapshot,
    referrer: string | null
  ): void {
    const user = this._analytics.user;

    const context = this._getContext();

    const properties = {
      appCode: user?.traits?.['TenantCode'],
      appId: user?.traits?.['TenantId'],
      referrer,
      nativeApp: false,
      ...this._analytics.globalOverrides,
      name: snapshot.data?.['analytics']?.page,
      search: context.page?.search,
      path: context.page?.path,
      title: context.page?.title,
      url: context.page?.url,
    };

    const eventPayloads = [
      {
        name: snapshot.data?.['analytics']?.page,
        properties,
        userId: user?.userId,
        originalTimestamp: new Date().toISOString(),
        type: 'page',
        context,
      },
    ];

    this._sender ??= this._injector.get(AsiStatsSender);
    this._sender.sendStats(this._ipAddress, eventPayloads);
  }

  private _track(events: TrackEvent<TrackEventData>[]): void {
    const user = this._analytics.user;
    const eventPayloads = events.map(({ action, properties }) => ({
      event: action,
      properties,
      userId: user?.userId,
      originalTimestamp: new Date().toISOString(),
      type: 'track',
      context: this._getContext(),
    }));

    this._sender ??= this._injector.get(AsiStatsSender);
    this._sender.sendStats(this._ipAddress, eventPayloads);
  }

  private _getContext() {
    if (ngServerMode) {
      return {};
    }
    return {
      page: {
        path: window.location.pathname,
        referrer: document.referrer,
        search: window.location.search,
        title: document.title,
        url: window.location.href,
      },
      userAgent: navigator.userAgent,
    };
  }
}

const ipAddressStateKey = makeStateKey<string>('ip_address');

export function provideAsiStats(): Provider {
  return global_isBrowser
    ? {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: () => {
          const asiStatsService = inject(AsiStatsService);
          return () => asiStatsService.initListeners();
        },
      }
    : {
        provide: APP_INITIALIZER,
        multi: true,
        useFactory: initAsiStatsIpCapturer,
      };
}

export function initAsiStatsIpCapturer(): () => void {
  const request = inject(REQUEST, { optional: true });
  // We previously used the `request-ip` package to get the client's connecting IP address.
  // The problem is that it checks all possible headers.
  // Here's the first condition: https://github.com/pbojinov/request-ip/blob/master/lib/index.js#L41
  // It reads the `x-client-ip` header.
  // The Node.js server deployed to Azure receives the following headers in the request:
  // "x-client-ip": "172.68.238.78"
  // "cf-connecting-ip": "193.19.185.177"
  // We have to retrieve the `cf-connecting-ip` (not the `x-client-ip`).
  // https://developers.cloudflare.com/fundamentals/reference/http-headers/#cf-connecting-ip
  const ipAddress = request?.headers.get('cf-connecting-ip');
  const transferState = inject(TransferState);

  return () => {
    if (ipAddress) {
      transferState.set(ipAddressStateKey, ipAddress);
    }
  };
}
